'use client';

import BackIconBlack from '@/assets/icons/back-black-icon.png';
import ShareIconBlack from '@/assets/icons/ShareIconBlack.svg';
import FeedbackSVGBlack from '@/assets/icons/feedbackBlack.svg';
import { useTranslation } from '@/i18n/client';
import { uaParse } from '@/intl-jdi-common-ssr/utils/uaParser';
import cn from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import './index.scss';
import { getCmsShareText, ItemKetTypeEnum } from '@/apis/cms/apiCms';
import { SourceType } from '@/components/globalFeedbackDrawer/types';
import FeedbackTrigger from '@/components/feedbackTrigger';

interface IDATAStyle {
  // 圆角间距
  radiusMargin?: {
    top?: number; // 上边距
    bottom?: number; // 下边距
    radius?: number; // 圆角
    leftRight?: number; // 左右边距
  };
}
interface UKH5TitleBarProps {
  moduleData: any;
  seo: any;
  customBackButton?: boolean;
  defaultShowTitle?: boolean;
  customBackHref?: string;
  shareClickPoint?: () => void;
}
export default function Module({
  moduleData,
  seo,
  customBackButton = false,
  defaultShowTitle = false,
  customBackHref = '',
  shareClickPoint,
}: UKH5TitleBarProps) {
  const { activityHeaderDarkMode } = seo ?? {};
  const [currentStyle, setCurrentStyle] = useState({
    titleBackgroundColor: '#fff',
    currentImage: BackIconBlack, // 滚动时使用的图片
    nameColor: 'black',
    shareIcon: <ShareIconBlack />,
    feedbackIcon: <FeedbackSVGBlack style={{ marginLeft: '12px' }} />,
  });

  const { t } = useTranslation();
  const router = useRouter();
  const { isInOchamaApp } = uaParse();

  const [isApp, setIsApp] = useState(false);
  const { dataBatch } = moduleData;

  /**
   * 处理返回事件
   */
  const backHandler = async () => {
    if (isApp) {
      const bridgeModule = await import('@/utils/bridge');
      const JSBridge = bridgeModule.JSBridge;
      JSBridge.functionInApp({
        method: 'back',
      });
    } else if (isInOchamaApp) {
      if (document?.referrer) {
        router.back();
      } else {
        location.href = '/';
      }
    } else {
      router.back();
      return;
    }
  };

  useEffect(() => {
    const { isInJoybuyApp } = uaParse();
    if (isInJoybuyApp) {
      setIsApp(true);
    }
  }, [setIsApp]);

  /**
   * 分享网页到应用
   * @param 无
   * @returns 无
   */

  const timer = useRef<any>(null);
  const shareToApp = (() => {
    return async () => {
      if (timer.current) return null;
      timer.current = setTimeout(() => {
        timer.current = null;
      }, 1000);
      try {
        let shareTitle = '';
        if (seo?.shareGuidingText && seo?.shareGuidingText.length > 0) {
          shareTitle = seo.shareGuidingText + ' ';
        } else {
          try {
            const res = await getCmsShareText(ItemKetTypeEnum.TEXT, 'cms.campaign.share.text');
            // 根据实际API响应结构取值，假设为 res?.data?.[0]?.value
            shareTitle =
              res?.data && res.data?.configList && res.data?.configList.length > 0
                ? res.data?.configList[0]?.value
                : `${t('share.text')}`;
          } catch {
            shareTitle = `${t('share.text')}`;
          }
        }
        const bridgeModule = await import('@/utils/bridge');
        const JSBridge = bridgeModule.JSBridge;
        JSBridge.functionInApp({
          method: 'shareWebPage',
          payload: {
            shareTitle: shareTitle,
            shareLink: window.location.href,
          },
        });
        shareClickPoint?.();
      } catch (e) {
        clearTimeout(timer.current);
        timer.current = null;
        throw e;
      }
    };
  })();

  const getTitleStyle = () => {
    if (isInOchamaApp) {
      return {
        marginTop: '0px',
      };
    }
    return {};
  };

  const getTitleBarStyle = () => {
    if (isInOchamaApp) {
      return {
        height: '44px',
        marginTop: '0px',
        backgroundColor: currentStyle.titleBackgroundColor,
      };
    }
    return {
      backgroundColor: currentStyle.titleBackgroundColor,
    };
  };

  const getCurrentImage = () => {
    if (isInOchamaApp) {
      return BackIconBlack;
    }
    return currentStyle.currentImage;
  };

  const getNameColor = () => {
    if (isInOchamaApp) {
      return { color: 'black' };
    }
    return { color: currentStyle.nameColor };
  };

  return (
    <div id='h5-titlebar-static-container'>
      <div className={cn('h5-titlebar', { app: isApp })} style={getTitleBarStyle()}>
        <div className='title' style={getTitleStyle()}>
          <button
            onClick={() => {
              if (customBackButton) {
                location.href = customBackHref;
              } else {
                backHandler();
              }
            }}
          >
            <Image className='img' src={getCurrentImage()} alt='' />
          </button>
          <div className='name' style={getNameColor()}>
            {dataBatch.titleSetting && dataBatch.titleSetting[0] && dataBatch.titleSetting[0].name}
          </div>
          {!isInOchamaApp && (
            <button className='shareIcon' onClick={shareToApp}>
              {currentStyle.shareIcon}
            </button>
          )}
          <FeedbackTrigger sourceType={SourceType.Marketing_Feedback}>
            {currentStyle.feedbackIcon}
          </FeedbackTrigger>
        </div>
      </div>
      <div className='spacer'></div>
    </div>
  );
}
